import type { RemediationProposal, RemediationProposalStatus } from "../../api/types.js";

export type RemediationQueueBucket = "open" | "approved" | "closed";

const OPEN_STATUSES = new Set<RemediationProposalStatus>([
  "draft",
  "previewed",
  "generating",
  "verifying",
  "ready",
]);

const CLOSED_STATUSES = new Set<RemediationProposalStatus>(["rejected", "rolled_back", "expired"]);

export function proposalBucket(status: RemediationProposalStatus): RemediationQueueBucket {
  if (status === "approved") return "approved";
  if (CLOSED_STATUSES.has(status)) return "closed";
  return "open";
}

export function countProposalBuckets(items: readonly RemediationProposal[]): {
  open: number;
  approved: number;
  closed: number;
} {
  const counts = { open: 0, approved: 0, closed: 0 };
  for (const item of items) counts[proposalBucket(item.status)] += 1;
  return counts;
}

export function filterProposals(
  items: readonly RemediationProposal[],
  bucket: RemediationQueueBucket | "all",
  findingId?: string | null,
): RemediationProposal[] {
  const wantedFinding = findingId?.trim() ?? "";
  return items.filter((item) => {
    if (bucket !== "all" && proposalBucket(item.status) !== bucket) return false;
    if (wantedFinding && item.findingId !== wantedFinding) return false;
    return true;
  });
}

export function remediationHeadline(counts: {
  open: number;
  approved: number;
  closed: number;
}): string {
  if (counts.open + counts.approved + counts.closed === 0) {
    return "No governed proposals are on file. That is an empty queue, not a clean estate.";
  }
  if (counts.open > 0) {
    return `${counts.open} proposal${counts.open === 1 ? "" : "s"} still need a review decision. Source is never mutated from this page.`;
  }
  if (counts.approved > 0) {
    return "Open work is empty. Approved plans remain preview-only until a later governed apply path exists.";
  }
  return "Every recorded proposal is closed. Closed is not the same as verified in production.";
}
